import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  useToast,
  useColorModeValue,
  Box,
  Text,
} from '@chakra-ui/react'
import { useUpdateGallery } from 'queries/gallery.query'
import Dropzone from '../../settings/components/Dropzone'
import { getStorageImageUrl } from 'utils/imageUrl'

export function EditGalleryModal({ isOpen, onClose, item }) {
  const toast = useToast()
  const { mutate, isPending } = useUpdateGallery()

  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [title, setTitle] = useState('')
  const [category, setCategory] = useState('none')
  const [customCategory, setCustomCategory] = useState('')
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    if (item) {
      setTitle(item.title || '')
      if (!item.category) {
        setCategory('none')
        setCustomCategory('')
      } else if (['concert', 'cultural', 'sports'].includes(item.category)) {
        setCategory(item.category)
        setCustomCategory('')
      } else {
        setCategory('other')
        setCustomCategory(item.category)
      }
      setIsActive(item.is_active === true || item.is_active === 1)
      setImagePreview(getStorageImageUrl(item.file_path))
      setImageFile(null)
    }
  }, [item])

  useEffect(() => {
    return () => {
      if (imageFile && imagePreview) {
        URL.revokeObjectURL(imagePreview)
      }
    }
  }, [imagePreview, imageFile])

  const handleFileChange = file => {
    setImageFile(file)
    if (file) {
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const formData = new FormData()
    if (imageFile) {
      formData.append('image', imageFile)
    }
    const finalCategory = category === 'other' ? customCategory : (category === 'none' ? '' : category)
    formData.append('category', finalCategory)
    formData.append('title', title)
    formData.append('is_active', isActive ? 1 : 0)

    mutate({ id: item.id, updatedData: formData }, {
      onSuccess: () => {
        toast({
          title: 'Gallery Image updated.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        })
        onClose()
      },
      onError: error => {
        const backendMessage = error.response?.data?.message || error.response?.data?.error || error?.message || 'An error occurred while updating.'
        toast({
          title: 'Update failed.',
          description: backendMessage,
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top',
        })
      },
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Gallery Image</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form id="edit-gallery-form" onSubmit={handleSubmit}>
            <FormControl mb={4}>
              <FormLabel>Replace Image (Optional)</FormLabel>
              <Dropzone
                w={{ base: '100%', '2xl': '100%' }}
                me='36px'
                maxH={{ base: '60%', lg: '50%', '2xl': '100%' }}
                minH={{ base: '60%', lg: '50%', '2xl': '100%' }}
                title='Upload New Photo'
                preview={imagePreview}
                onFileChange={handleFileChange}
              />
            </FormControl>

            <FormControl mb={4}>
              <FormLabel>Title (Optional)</FormLabel>
              <Input
                placeholder="e.g. Live at Hamburg 2026"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </FormControl>

            <FormControl mb={4}>
              <FormLabel>Category</FormLabel>
              <Select value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="none">None</option>
                <option value="concert">Concert</option>
                <option value="cultural">Cultural</option>
                <option value="sports">Sports</option>
                <option value="other">Other</option>
              </Select>
            </FormControl>

            {category === 'other' && (
              <FormControl mb={4} isRequired>
                <FormLabel>Custom Category Name</FormLabel>
                <Input
                  placeholder="e.g. Exhibitions"
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                />
              </FormControl>
            )}

            <FormControl display="flex" alignItems="center" mb={4}>
              <FormLabel htmlFor="edit-is-active" mb="0">
                Active on Website
              </FormLabel>
              <Switch
                id="edit-is-active"
                isChecked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                colorScheme="brand"
              />
            </FormControl>
          </form>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="brand" mr={3} type="submit" form="edit-gallery-form" isLoading={isPending}>
            Update
          </Button>
          <Button variant="ghost" onClick={onClose} isDisabled={isPending}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
